import {
    startTransition,
    useCallback,
    useEffect,
    useMemo,
    useRef,
} from 'react';
import type { FilterStates } from '@rocco/ui/filter-sort/view-models/filter-states';
import type { FiltersConfig } from '@rocco/ui/filter-sort/types/filter-config-section';
import type {
    FilterStates as RoccoFilterStates,
    FilterStatesKey,
} from '@modules-rocco/filters/types/filter-states';
import type { SearchIndexes } from '@modules-rocco/search/types/enum/search-indexes';
import { useLocation, useNavigate } from 'react-router';
import { useTracker } from '@archipro-website/tracker';
import { reportFilterApplied } from '@modules-rocco/filters/tracking/report-filter-applied';
import { mapFilterStatesToProfessionalTabURL } from '@modules-rocco/filters/data-mappers/filter-states-to-professional-tab-url';
import { useSorts } from '@ui-rocco/hooks/use-sorts';
import { useAggregation } from '@ui-rocco/hooks/use-aggregation';
import { useSearchFilters } from '@rocco/ui/filter-sort/hooks/use-search-filters';
import { toggleMultipleListFilterStateItem } from '@modules-rocco/filters/filter-state/multiple-values-filter-state';
import { toggleSearchableListFilterStateItem } from '@modules-rocco/filters/filter-state/searchable-list-filter-state';
import { useCurrentLocation } from '@ui-rocco/hooks/location/use-current-location';
import { usePlaceFinder } from '@ui-rocco/hooks/location/use-place-finder';
import {
    FindPlaceTypeEnum,
    PermissionEnum,
    type SupportedCountry,
} from 'generated/graphql';
import { useRootData } from '@modules/root';
import { useUser } from '~/modules/user/hook/use-user';
import { getPermissions } from '@modules/user';
import type { SearchSortOptions } from '@modules-rocco/search/types/enum/search-sort-options';
import type { SearchServiceQueryFilters } from '@modules-rocco/search/types/api';

/**
 * Filter keys that never surface on a professional list tab: the tab is
 * already scoped to one professional, so professional-scoping facets are
 * ignored when writing the URL.
 */
const TAB_IGNORE_FILTER_KEYS: FilterStatesKey[] = [
    'professionals',
    'professionalCategories',
    'professions',
];

interface UseProfessionalTabFilterSortArgs {
    config: FiltersConfig;
    searchIndex: SearchIndexes;
    initialFilterStates: FilterStates;
    /** Extra keys to keep out of the URL (e.g. viewProjectsBy when hidden). */
    extraIgnoreFilterKeys?: FilterStatesKey[];
    /** Strip viewProjectsBy from the URL when it equals this default. */
    defaultViewProjectsBy?: string;
    /**
     * ID-based scope filters the aggregate endpoint needs but the URL can't
     * carry (partners' professionalParents, case-studies' professionalsUsed).
     */
    aggregateScopeFilters?: SearchServiceQueryFilters;
}

/**
 * The professional list tabs' equivalent of `useCategoryFilterSort`: drives
 * the Rocco filter sidebar with the real Rocco search-filters engine while
 * writing URLs in the tabs' existing format (plain query params on the tab
 * pathname — no browse param, no path-encoded sort).
 */
export const useProfessionalTabFilterSort = ({
    config,
    searchIndex,
    initialFilterStates,
    extraIgnoreFilterKeys,
    defaultViewProjectsBy,
    aggregateScopeFilters,
}: UseProfessionalTabFilterSortArgs) => {
    const { localisation } = useRootData();

    const user = useUser();
    const hasMarketingView = getPermissions(
        user,
        PermissionEnum.BmMarketingView,
    );

    const sortOptions = useSorts(searchIndex ?? null);
    const defaultSort = (
        sortOptions.find((option) => option.default) ?? sortOptions[0]
    )?.value;

    const ignoreFilterKeys = useMemo(
        () => [...TAB_IGNORE_FILTER_KEYS, ...(extraIgnoreFilterKeys ?? [])],
        [extraIgnoreFilterKeys],
    );

    const { handleRefetch } = useProfessionalTabNavigation({
        ignoreFilterKeys,
        defaultSort,
        defaultViewProjectsBy,
    });

    // The aggregate destination URL deliberately keeps the ignored keys —
    // the injected professionals scope must reach the aggregate endpoint
    // even though it never appears in the address bar.
    const buildDestinationURL = useCallback(
        (filterStates: RoccoFilterStates, currentURL: string) =>
            mapFilterStatesToProfessionalTabURL(filterStates, currentURL, {
                defaultSort,
                defaultViewProjectsBy,
            }),
        [defaultSort, defaultViewProjectsBy],
    );

    const { handleAggregate, resetAggregationQueue, settledDestinationURL } =
        useAggregation({
            searchIndex,
            visibilityType: initialFilterStates.visibilityType,
            defaultSort,
            buildDestinationURL,
            scopeFilters: aggregateScopeFilters,
        });

    const {
        currentLocation,
        getCurrentLocation,
        loading: currentLocationLoading,
    } = useCurrentLocation();

    const {
        places,
        handlePlaceFind,
        resetPlaces,
        loading: placeFinderLoading,
    } = usePlaceFinder({
        type: FindPlaceTypeEnum.SearchFilter,
        country: localisation.country as SupportedCountry,
    });

    const searchFilters = useSearchFilters({
        searchIndex,
        hasAdminPermission: hasMarketingView,
        config,
        initialStates: initialFilterStates,
        isCategoryPage: false,
        onRefetch: handleRefetch,
        onAggregate: handleAggregate,
        onAggregateQueueClear: resetAggregationQueue,
        onToggleMultipleValues: toggleMultipleListFilterStateItem,
        onToggleSearchableList: toggleSearchableListFilterStateItem,
        locationService: {
            country: localisation.country,
            currentLocation: {
                isLoading: currentLocationLoading,
                location: currentLocation,
                onCurrentLocationFetch: getCurrentLocation,
            },
            keywordLocations: {
                isLoading: placeFinderLoading,
                locations: places,
                onKeywordResetPlaces: resetPlaces,
                onKeywordLocationsFetch: handlePlaceFind,
            },
        },
    });

    return useMemo(
        () => ({
            searchFilters,
            sortOptions,
            settledDestinationURL,
        }),
        [searchFilters, sortOptions, settledDestinationURL],
    );
};

interface UseProfessionalTabNavigationOptions {
    ignoreFilterKeys: FilterStatesKey[];
    defaultSort?: SearchSortOptions;
    defaultViewProjectsBy?: string;
    /** Debounce delay in ms for URL navigation, coalescing rapid toggles. */
    debounceMs?: number;
}

const useProfessionalTabNavigation = ({
    ignoreFilterKeys,
    defaultSort,
    defaultViewProjectsBy,
    debounceMs = 150,
}: UseProfessionalTabNavigationOptions) => {
    const location = useLocation();
    const navigate = useNavigate();
    const tracker = useTracker();
    const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const lastOwnNavigationRef = useRef<string | null>(null);

    // A pending commit must never navigate an unmounted panel — useNavigate
    // still fires after unmount.
    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
                debounceTimerRef.current = null;
            }
        };
    }, []);

    // A pending debounced navigation is built from the location at schedule
    // time, so a FOREIGN navigation (category link, back button) invalidates
    // it. Our own commit flushing through the router must not — a toggle
    // scheduled in that window still has to reach the URL.
    useEffect(() => {
        if (
            lastOwnNavigationRef.current ===
            location.pathname + location.search
        ) {
            return;
        }
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = null;
        }
    }, [location.pathname, location.search]);

    const handleRefetch = useCallback(
        (states: FilterStates) => {
            const originalURL = location.pathname + location.search;
            const newURL = mapFilterStatesToProfessionalTabURL(
                states as RoccoFilterStates,
                originalURL,
                { ignoreFilterKeys, defaultSort, defaultViewProjectsBy },
            );

            // Cancel any pending navigation before the no-op check: states
            // that returned to what the URL already shows (a toggle undone
            // within the debounce window) must not replay the earlier commit.
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
                debounceTimerRef.current = null;
            }

            const [newURLWithoutHash] = newURL.split('#');
            if (newURLWithoutHash === originalURL) return;

            // Reported as the change lands rather than as it is scheduled: a
            // toggle within the debounce window replaces the pending commit,
            // and reporting each would count the first facet twice.
            const commit = () => {
                const committedURL = newURLWithoutHash ?? newURL;
                reportFilterApplied(
                    tracker,
                    originalURL,
                    committedURL,
                    true,
                    'professionalTab',
                );
                lastOwnNavigationRef.current = committedURL;
                startTransition(() => {
                    navigate(newURL, { preventScrollReset: true });
                });
            };

            if (debounceMs > 0) {
                debounceTimerRef.current = setTimeout(commit, debounceMs);
            } else {
                commit();
            }
        },
        [
            location.pathname,
            location.search,
            ignoreFilterKeys,
            navigate,
            defaultSort,
            defaultViewProjectsBy,
            debounceMs,
            tracker,
        ],
    );

    return { handleRefetch };
};
